import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  background: "linear-gradient(195deg,  #004030, #191919)",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    minHeight: "40vh",
    flex: "none",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "35vh",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -200,
    left: -200,
    width: 400,
    height: 400,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "50%",
    [theme.breakpoints.down("md")]: {
      bottom: -150,
      left: -150,
      width: 300,
      height: 300,
    },
    [theme.breakpoints.down("sm")]: {
      bottom: -100,
      left: -100,
      width: 200,
      height: 200,
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -100,
    left: -100,
    width: 250,
    height: 250,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: "50%",
    [theme.breakpoints.down("md")]: {
      bottom: -75,
      left: -75,
      width: 150,
      height: 150,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: "100vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    minHeight: "60vh",
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "65vh",
    padding: theme.spacing(1),
  },
}));

const BrandContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  color: "white",
  maxWidth: 400,
  padding: theme.spacing(0, 4),
  textAlign: "left",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0, 3),
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2),
  },
}));

const LoginForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  backgroundColor: "white",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  borderRadius: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
    margin: theme.spacing(0, 1),
    borderRadius: theme.spacing(1),
  },
}));

export default function ForgotPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const navigate = useNavigate();
  const handleClose = () => setState({ ...state, open: false });
  const { vertical, horizontal, open, message, severity } = state;
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const formik = useFormik({
    initialValues: {
      txtEmail: "",
      txtPassword: "",
      txtConfirmPassword: "",
    },
    validationSchema: Yup.object({
      txtEmail: Yup.string().email("Invalid email").required("Email is required"),
      txtPassword: Yup.string().required("Password is required"),
      txtConfirmPassword: Yup.string()
        .oneOf([Yup.ref("txtPassword")], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // 1. Get current user details
        // const meRes = await axios.post(
        //   `${process.env.REACT_APP_API_URL}/auth/me`,
        //   {},
        //   { withCredentials: true }
        // );
        // const user = meRes.data;

        // 2. Construct actual data
        // const payload = {
        //   user_id: user.user_id,
        //   user_name: user.user_name,
        //   email: user.email,
        //   role_id: user.role_id,
        //   district: user.district,
        //   new_password: values.txtPassword,
        // };
        const payload = {
          email: values.txtEmail,
          new_password: values.txtPassword,
        };

        // 3. Send password update request
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
            // withCredentials: true,
          }
        );

        // 4. Success flow
        if (response.data.status === 200) {
          setState({
            ...state,
            open: true,
            severity: "success",
            message: response.data.message,
          });

          setTimeout(() => {
            navigate("/");
          }, 2000);
        }
      } catch (error) {
        console.error("Error:", error);
        setState({
          ...state,
          open: true,
          severity: "error",
          message:
            error.response?.data?.message || error.response?.data?.error || "Something went wrong",
        });
      } finally {
        setLoading(false); // hide loader
      }
    },
  });

  return (
    <MainContainer>
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <LeftPanel>
        <BrandContent>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: { xs: 1, sm: 2 },
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
                lg: "3.5rem",
              },
            }}
            style={{ color: "white" }}
          >
            CMGFP
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: { xs: 2, sm: 3, md: 6 },
              lineHeight: 1.4,
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
                md: "1.25rem",
              },
              px: { xs: 1, sm: 0 },
            }}
            style={{ color: "white", fontWeight: "700", fontSize: "15px" }}
          >
            {"Chief Minister's Green Fellowship Programme"}
          </Typography>
          {/* <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: "white",
                    borderColor: "white",
                    "&:hover": {
                      backgroundColor: "white",
                      color: "#1976d2",
                      borderColor: "white",
                    },
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    display: { xs: "none", sm: "inline-flex", md: "inline-flex" },
                  }}
                >
                  Read More
                </Button> */}
        </BrandContent>
      </LeftPanel>

      <RightPanel>
        <Container
          sx={{ px: { xs: 1, sm: 3 } }}
          display="flex"
          // justifyContent="center"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <LoginForm elevation={0}>
            <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: "text.primary",
                  fontSize: {
                    xs: "1.75rem",
                    sm: "2rem",
                    md: "2.125rem",
                  },
                }}
              >
                Reset Password
              </Typography>
            </Box>

            <Box component="form" sx={{ mt: { xs: 2, sm: 3 } }}>
              <TextField
                error={formik.errors.txtEmail}
                fullWidth
                label="Email"
                onChange={formik.handleChange("txtEmail")}
                helperText={formik.errors.txtEmail}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 48, sm: 56 },
                    backgroundColor: "white",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
              <TextField
                error={formik.errors.txtPassword}
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                onChange={formik.handleChange("txtPassword")}
                helperText={formik.errors.txtPassword}
                size="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "hide the password" : "display the password"}
                        onClick={handleClickShowPassword}
                        // onMouseDown={handleMouseDownPassword}
                        // onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <IconEye sx={{ color: "text.secondary" }} />
                        ) : (
                          <IconEyeOff sx={{ color: "text.secondary" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 48, sm: 56 },
                    backgroundColor: "white",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                onChange={formik.handleChange("txtConfirmPassword")}
                helperText={formik.errors.txtConfirmPassword}
                error={formik.errors.txtConfirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <IconEye /> : <IconEyeOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: { xs: 1.5, sm: 2 } }}
              />

              <Button
                onClick={() => formik.handleSubmit()}
                fullWidth
                variant="contained"
                // color="primary"
                size="large"
                style={{ color: "white" }}
                sx={{
                  height: { xs: 48, sm: 56 },
                  backgroundColor: "#004030bf",
                  "&:hover": {
                    backgroundColor: "#004030",
                  },
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 500,
                  mb: { xs: 2, sm: 3 },
                }}
                disabled={loading}
              >
                Update Password
              </Button>

              
            </Box>
          </LoginForm>
        </Container>
      </RightPanel>
    </MainContainer>
  );
}
